import { useState, useEffect, useMemo, useRef, useCallback } from 'preact/hooks';
import { Fragment } from 'preact';
import type { SessionSummary, SearchResult, ProjectPlanSummary } from '@voice-assistant/shared';
import { CASCADE_MODEL_ID } from '@voice-assistant/shared';
import { openDrawer, useCascadeGatePending, clearCascadeGate } from '../state/drawerStore';
import {
  useUiSessionListTab,
  setSessionListTab,
  useUiState,
  getUiState,
  setSessionListSort,
  setSessionListFilters,
  setSessionListIssueFilter,
  setSessionListScroll,
} from '../state/uiStateStore';
import { show as coachShow, markDiscovered as coachMarkDiscovered } from '../state/coachStore';
import { useAwaitingDeploy } from '../state/waveStore';
import { useSettingsStore } from '../state/settings.store';
import { modelBadge, modelShortName, timeAgo } from '../utils/modelHelpers';
import { useModels } from '../hooks/useModels';
import { fabModelGroups } from '../shared-ui/ModelPickerList';
import { BeetleLogbookButton } from './BeetleLogbookButton';
import { GearMenu } from './GearMenu';
import { PlansTab } from './PlansTab';
import { RoadsTab } from './RoadsTab';
import { useSwipeTabs } from '../hooks/useSwipeTabs';
import { haptic } from '../utils/haptic';
import { formatDeployPending } from '../utils/deployStatusLabel';
import { computeFabMenuMaxHeightPx } from '../utils/fabMenuHeight';
import { getBrand, getBrandId } from '../brands';
import { useBusySessionIds } from '../state/composerStore';
import {
  isAgentSource,
  useActiveAgentCount,
  useActiveUserCount,
  useAgentSessions,
  useAllSessions,
  useAnyBusyListed,
  useUserSessions,
} from '../state/sessionListStore';

function highlightMatch(text: string, query: string): any {
  if (!query.trim()) return text;
  const words = query.trim().split(/\s+/).filter(Boolean);
  const pattern = new RegExp(`(${words.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi');
  const parts = text.split(pattern);
  return parts.map((part) =>
    pattern.test(part) ? <mark class="search-highlight">{part}</mark> : part
  );
}

function truncateAround(text: string, query: string, maxLen = 200): string {
  const lower = text.toLowerCase();
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  let bestIdx = 0;
  for (const w of words) {
    const idx = lower.indexOf(w);
    if (idx >= 0) { bestIdx = idx; break; }
  }
  const start = Math.max(0, bestIdx - 60);
  const end = Math.min(text.length, start + maxLen);
  let snippet = text.slice(start, end);
  if (start > 0) snippet = '…' + snippet;
  if (end < text.length) snippet += '…';
  return snippet;
}

/* ── FAB model picker — driven by model registry (no hardcoded IDs) ── */

/* ── Props ─────────────────────────────────────────────────────────── */

interface Props {
  // Phase 4: `sessions` prop removed — SessionListView reads from
  // sessionListStore directly (see useAllSessions hook in the body).
  currentSessionId: string | null;
  onSelectSession: (sessionId: string) => void;
  onRename: (sessionId: string, name: string) => void;
  onDeleteRequest: (ids: string[]) => void;
  onSwitchToMessage?: (sessionId: string, messageId: string) => void;
  onSettings: () => void;
  onContinue?: (sessionId: string) => void;
  isSessionSoundOn: (id: string) => boolean;
  onToggleSound: (id: string) => void;
  connected: boolean;
  speaking: boolean;
  toolActivities: any[];
  deployStatus: string;
  deployPending?: { deferCount: number; firstRequestedAt: string | null } | null;
  theme: string | null;
  onThemeSwitch: (letter: string) => void;
  userEmail: string | null;
  onLogout: () => void;
  onBrowser: () => void;
  sshConnected?: boolean;
  onSshConnect?: () => void;
  onSshDisconnect?: () => void;
  memosEnabled?: boolean;
  lessonGeneratorEnabled?: boolean;
  billingEnabled?: boolean;
  isAdmin?: boolean;
  onDoctor?: () => void;
  onRebuild?: () => void;
  rebuildEnabled?: boolean;
  onRunCommand?: (cmd: string) => void;
  onNewSession?: (model?: string) => void;
  /** History-aware open of the unified Files drawer (drawerStore). Falls back to a
   *  plain openDrawer('files') when absent. */
  onFiles?: () => void;
  // Feature-surface tab gates (SE-PF4). Default true when omitted. Combined with
  // getBrand().voiceOnlyMode for the developer tabs (Plans/Roads). Tasks/Usage/Ops
  // moved to bottom-nav-only (issue #5, coach-tooltip-system.md Phase 5).
  plansTabEnabled?: boolean;
  roadsTabEnabled?: boolean;
  onPinSession?: (sessionId: string) => void;
  onUnpinSession?: (sessionId: string) => void;
  onLogbook?: () => void;
  browserPanelEnabled?: boolean;
  pulseEnabled?: boolean;
  guideEnabled?: boolean;
  /** Protofac (SE-PF6 #1391): plans read from the user's sandbox over SSH. */
  sandboxFsEnabled?: boolean;
  // settings-zustand-migration Phase 4: beetleVisible removed from props — the
  // SessionListView now subscribes to useSettingsStore directly.
}

/* ── Component ─────────────────────────────────────────────────────── */

export function SessionListView({
  currentSessionId,
  onSelectSession,
  onRename, onDeleteRequest, onSwitchToMessage,
  onSettings, onContinue,
  isSessionSoundOn, onToggleSound,
  connected, speaking, toolActivities, deployStatus, deployPending,
  theme, onThemeSwitch,
  userEmail, onLogout, onBrowser, sshConnected, onSshConnect, onSshDisconnect,
  memosEnabled, lessonGeneratorEnabled, billingEnabled, isAdmin, onDoctor, onRebuild, rebuildEnabled, onRunCommand, onNewSession,
  plansTabEnabled = true, roadsTabEnabled = true,
  onPinSession, onUnpinSession,
  onLogbook, onFiles,
  browserPanelEnabled, pulseEnabled, guideEnabled, sandboxFsEnabled,
}: Props) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { models } = useModels();
  // Phase 4: beetleVisible from store.
  const beetleVisible = useSettingsStore((s) => s.beetleVisible);

  // `busySetArr`/`busySet` still used by search-result filtering (server
  // results are NOT in sessionListStore, so we cross-ref against the busy ids
  // from composerStore directly). Per-session derived counts have moved to
  // the FSM hooks above (useActiveAgentCount / useActiveUserCount).
  const busySetArr = useBusySessionIds();
  const busySet = useMemo(() => new Set(busySetArr), [busySetArr]);
  // Phase 4: all sessions (was the legacy `sessions` prop). Used by select-all,
  // select-older-than, and aggregate counts (all/d1/d3/d7) below — the only
  // residual reads that need the full set, not the user-vs-agent split.
  const sessions = useAllSessions();

  // Derive FAB model groups from the registry via the SHARED census deriver (fabModelGroups →
  // groupByProvider, the same grouping the desktop caret picker and ModelControlSheet use) —
  // provider N+1 appears here with NO frontend edit (model-picker-provider-parity, #1973).
  // Cascade Agent stays excluded (inside the deriver): FAB +tap is the only door to a cascade
  // session (long-press is "pick a fixed model"). Two doors → confusion.
  const fabGroups = useMemo(() => fabModelGroups(models), [models]);

  // FAB new-session long-press → model picker
  const [fabPickerOpen, setFabPickerOpen] = useState(false);
  // Reactive cascade tier-gate cue from drawerStore (replaces the old window-event
  // listener — see the effect below). Read as a selector so a FRESH mount (the drawer
  // was just bounced from closed/another panel to sessions) still sees a pending gate.
  const cascadeGatePending = useCascadeGatePending();
  const fabLongPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fabIsLongPress = useRef(false);
  const fabWrapRef = useRef<HTMLDivElement>(null);
  const fabMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!fabPickerOpen) return;
    const onDown = (e: PointerEvent) => {
      if (fabWrapRef.current && !fabWrapRef.current.contains(e.target as Node)) {
        setFabPickerOpen(false);
      }
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [fabPickerOpen]);

  // Phase 9: cascade tier gate — backend denied model='cascade', open fixed picker.
  // Migrated to the coach substrate (Phase 3b, kind:'toast' — recurs on every
  // gate-denial, not seen-gated; a free user tapping Cascade a 2nd time still hears
  // it, unlike a `coach` row which would only ever fire once).
  //
  // Reliability fix (fix/cascade-gated-drawer-panel-swap): driven by the REACTIVE
  // `cascadeGatePending` drawerStore flag instead of a synchronous `cascade-gated` window
  // event. When the drawer was closed OR open on another panel (e.g. Usage), this
  // component wasn't mounted, so its old window listener registered AFTER the event fired
  // and the hint was missed. Reading store state means a fresh mount sees the pending gate
  // and shows the hint; we then clearCascadeGate() so it doesn't re-fire on later renders.
  useEffect(() => {
    if (!cascadeGatePending) return;
    setFabPickerOpen(true);
    coachShow({ id: 'cascade-gated', kind: 'toast', body: 'Cascade Agent requires Pro — pick a fixed model', ttlMs: 5000 });
    clearCascadeGate();
  }, [cascadeGatePending]);

  const handleFabPointerDown = () => {
    fabIsLongPress.current = false;
    fabLongPressTimer.current = setTimeout(() => {
      fabIsLongPress.current = true;
      setFabPickerOpen(true);
      // Real usage of the long-press-to-pick gesture (coach-tooltip-system.md §5.2) —
      // cancel the model-picker tip mid-display + mark seen.
      coachMarkDiscovered('model-picker');
    }, 400);
  };

  const handleFabPointerUp = () => {
    if (fabLongPressTimer.current) {
      clearTimeout(fabLongPressTimer.current);
      fabLongPressTimer.current = null;
    }
  };

  const handleFabClick = () => {
    if (fabIsLongPress.current) return;
    if (fabPickerOpen) { setFabPickerOpen(false); return; }
    // FAB +tap → Cascade Agent session (model = 'cascade'). Long-press picker
    // (handled separately) is the only door to fixed-model sessions.
    setTimeout(() => onNewSession?.(CASCADE_MODEL_ID), 0);
  };

  // Custom scroll spine
  const listRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);

  // Swipe-tab navigation — panelsViewportRef kept for applyPanelsSlide; state for useSwipeTabs
  const panelsViewportRef = useRef<HTMLDivElement | null>(null);
  const [panelsViewportEl, setPanelsViewportEl] = useState<HTMLDivElement | null>(null);
  const setPanelsViewport = useCallback((el: HTMLDivElement | null) => {
    panelsViewportRef.current = el;
    setPanelsViewportEl(el);
  }, []);
  const panelsTrackRef    = useRef<HTMLDivElement>(null);
  const tabsBarRef        = useRef<HTMLDivElement>(null);
  const indicatorRef      = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = listRef.current;
    const thumb = thumbRef.current;
    if (!el || !thumb) return;
    const update = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      if (scrollHeight <= clientHeight + 2) { thumb.style.opacity = '0'; return; }
      thumb.style.opacity = '1';
      const ratio = clientHeight / scrollHeight;
      const h = Math.max(28, ratio * clientHeight);
      const top = (scrollTop / (scrollHeight - clientHeight)) * (clientHeight - h);
      thumb.style.top = `${top}px`;
      thumb.style.height = `${h}px`;
    };
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    update();
    return () => { el.removeEventListener('scroll', update); ro.disconnect(); };
  }, []);

  // Class-B scroll restore (Phase 2, #1979): SessionListView mounts when the
  // Agents drawer opens (DrawerHost renders panels lazily) — rehydrate the
  // list's scroll offset so a reload returns the operator to where they were.
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const saved = getUiState().sessionListScroll;
    if (saved > 0) requestAnimationFrame(() => { el.scrollTop = saved; });
  }, []);

  // Save the scroll offset on scroll (debounced) — named store action only.
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    let timer: ReturnType<typeof setTimeout>;
    const onScroll = () => {
      clearTimeout(timer);
      timer = setTimeout(() => setSessionListScroll(el.scrollTop), 200);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => { el.removeEventListener('scroll', onScroll); clearTimeout(timer); };
  }, []);

  // Bound the model-picker menu height to the space ABOVE the FAB within the drawer
  // (coach-tooltip Phase 2 / Issue 1). The old `max-height:70vh` was viewport-relative,
  // so a growing provider list punched through the drawer header/search. We measure the
  // FAB wrap → top of the scrollable list (`.sl-list`, which sits below the search +
  // drawer header in flow, so bounding to it clears both) and write `--coach-fab-available`
  // (px) onto the menu, plus toggle `.is-scrollable` so the scroll-fade only shows when
  // content overflows. Re-measures on list/menu resize (keyboard open shrinks the drawer)
  // + window resize.
  useEffect(() => {
    if (!fabPickerOpen) return;
    const wrap = fabWrapRef.current;
    const menu = fabMenuRef.current;
    if (!wrap || !menu) return;
    const measure = () => {
      const wrapBottom = wrap.getBoundingClientRect().bottom;
      const list = listRef.current;
      const listTop = list ? list.getBoundingClientRect().top : 8;
      const available = computeFabMenuMaxHeightPx(wrapBottom, listTop);
      menu.style.setProperty('--coach-fab-available', `${available}px`);
      menu.classList.toggle('is-scrollable', menu.scrollHeight > menu.clientHeight + 1);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(menu);
    if (listRef.current) ro.observe(listRef.current);
    window.addEventListener('resize', measure);
    return () => { ro.disconnect(); window.removeEventListener('resize', measure); };
  }, [fabPickerOpen]);

  // Multi-select edit mode
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressTriggered = useRef(false);
  const trayAutoCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Swipe-to-reveal state (for pin/unpin/delete action tray)
  const [swipedRowId, setSwipedRowId] = useState<string | null>(null);

  const closeTray = useCallback(() => {
    if (trayAutoCloseTimerRef.current) { clearTimeout(trayAutoCloseTimerRef.current); trayAutoCloseTimerRef.current = null; }
    setSwipedRowId(null);
  }, []);

  const startAutoClose = useCallback(() => {
    if (trayAutoCloseTimerRef.current) clearTimeout(trayAutoCloseTimerRef.current);
    trayAutoCloseTimerRef.current = setTimeout(closeTray, 4000);
  }, [closeTray]);

  const handleDeleteRequest = useCallback((sessionId: string) => {
    closeTray();
    if ('vibrate' in navigator) navigator.vibrate([10, 30, 10]);
    onDeleteRequest([sessionId]);
  }, [closeTray, onDeleteRequest]);

  const enterSelectMode = useCallback((sessionId?: string) => {
    setSelectMode(true);
    setSelectedIds(sessionId ? new Set([sessionId]) : new Set());
    if ('vibrate' in navigator) navigator.vibrate(30);
  }, []);

  const exitSelectMode = useCallback(() => {
    setSelectMode(false);
    setSelectedIds(new Set());
  }, []);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(sessions.map(s => s.id)));
  }, [sessions]);

  const selectOlderThan = useCallback((days: number) => {
    const cutoff = Date.now() - days * 86400000;
    setSelectedIds(new Set(
      sessions.filter(s => new Date(s.updatedAt).getTime() < cutoff).map(s => s.id)
    ));
  }, [sessions]);

  const bulkCounts = useMemo(() => {
    const now = Date.now();
    return {
      all: sessions.length,
      d1: sessions.filter(s => new Date(s.updatedAt).getTime() < now - 86400000).length,
      d3: sessions.filter(s => new Date(s.updatedAt).getTime() < now - 3 * 86400000).length,
      d7: sessions.filter(s => new Date(s.updatedAt).getTime() < now - 7 * 86400000).length,
    };
  }, [sessions]);

  const deleteSelected = useCallback(() => {
    onDeleteRequest(Array.from(selectedIds));
    exitSelectMode();
  }, [selectedIds, onDeleteRequest, exitSelectMode]);

  const handleLongPressStart = useCallback((sessionId: string) => (e: PointerEvent) => {
    longPressTriggered.current = false;
    longPressTimer.current = setTimeout(() => {
      longPressTriggered.current = true;
      // If a tray is already open on a different row, enter multi-select mode
      if (swipedRowId && swipedRowId !== sessionId) {
        closeTray();
        enterSelectMode(sessionId);
        return;
      }
      // Otherwise, open Pin/Del tray for this row
      closeTray(); // close any other open tray first
      haptic(15);
      setSwipedRowId(sessionId);
      startAutoClose();
    }, 600);
  }, [enterSelectMode, swipedRowId, closeTray, startAutoClose]);

  const handleLongPressEnd = useCallback(() => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
  }, []);

  // Tab state: sessions vs the drawer's content-scoped developer tabs. Tasks/Usage/Ops moved
  // to bottom-nav-only (issue #5, coach-tooltip-system.md Phase 5) — they duplicated the
  // bottom nav's app-scoped Tasks/Usage/Ops slots exactly (same enablement flags), so this
  // drawer now owns only the content-scoped set: Sessions/Plans/Roads.
  // Tab is store-owned Class-B state (uiStateStore, `va-ui-state-v1`) — Phase 1 of
  // user-ui-preferences-state (#1566): survives reload. Read via selector, write via
  // the named action (R-A2). The persisted value may reference a tab gated OUT on
  // this profile/brand (plans/roads flags), so render normalizes to 'sessions' below
  // AFTER tabList is derived — the store keeps the operator's real preference.
  const storedTab = useUiSessionListTab();
  const setTab = setSessionListTab;

  // Tab list order + derived index. Built from the SAME conditions as the tab buttons
  // and panels below so swipe order, button visibility, and panel DOM order stay aligned
  // (the panel track translates by tabIndex × 100vw — any drift breaks the swipe math).
  // Developer tabs (Plans/Roads) need both the per-tab flag (SE-PF4) AND a non-voice brand.
  const tabList = useMemo(() => {
    const devOk = !getBrand().voiceOnlyMode;
    const list: Array<'sessions' | 'plans' | 'roads'> = ['sessions'];
    if (devOk && plansTabEnabled) list.push('plans');
    if (devOk && roadsTabEnabled) list.push('roads');
    return list;
  }, [plansTabEnabled, roadsTabEnabled]);
  // Normalize a gated-out persisted tab to 'sessions' for rendering (store value untouched).
  const tab: 'sessions' | 'plans' | 'roads' = tabList.includes(storedTab) ? storedTab : 'sessions';
  const tabIndex = tabList.indexOf(tab);
  // Lazy-mount helper: a panel should mount when the user is at it or one swipe away.
  // Derived from tabList (NOT a hardcoded index) so thresholds stay correct as tabs are
  // gated in/out by SE-PF4 — a hardcoded `tabIndex >= 3` breaks the moment Roads is removed.
  const nearTab = (name: typeof tabList[number]) => {
    const i = tabList.indexOf(name);
    return i >= 0 && (tabIndex === -1 ? 0 : tabIndex) >= i - 1;
  };

  // Position the indicator at a specific tab button (no animation)
  const snapIndicator = useCallback((idx: number) => {
    const tabsEl    = tabsBarRef.current;
    const indicator = indicatorRef.current;
    if (!tabsEl || !indicator) return;
    const buttons = tabsEl.querySelectorAll<HTMLElement>('.sl-tab');
    const btn     = buttons[idx];
    if (!btn) return;
    const tabsRect = tabsEl.getBoundingClientRect();
    const btnRect  = btn.getBoundingClientRect();
    indicator.style.transition = 'none';
    indicator.style.left  = `${btnRect.left - tabsRect.left}px`;
    indicator.style.width = `${btnRect.width}px`;
  }, []);

  // Translate the panels track (direct DOM write — no Preact state)
  const applyPanelsSlide = useCallback((idx: number, offset: number, animated: boolean) => {
    const track    = panelsTrackRef.current;
    const viewport = panelsViewportRef.current;
    if (!track || !viewport) return;
    // offsetWidth may be 0 before first paint; fall back to window.innerWidth
    // which is guaranteed to equal 100vw (the CSS panel width)
    const vw = viewport.offsetWidth || window.innerWidth;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.style.transition = (animated && !prefersReducedMotion)
      ? 'transform 240ms cubic-bezier(0.22, 0.68, 0.35, 1.0)'
      : 'none';
    track.style.transform = `translateX(${-(idx * vw) + offset}px)`;
  }, []);

  // Snap panels + indicator whenever the committed tab changes
  useEffect(() => {
    const idx = tabList.indexOf(tab);
    applyPanelsSlide(idx, 0, true);
    // Use rAF so the DOM has painted the indicator's container
    requestAnimationFrame(() => snapIndicator(idx));
  }, [tab, tabList, applyPanelsSlide, snapIndicator]);

  const issueFilter = useUiState((s) => s.sessionListIssueFilter);

  // Wire swipe-tab gesture
  useSwipeTabs({
    tabs: tabList,
    activeIndex: tabIndex === -1 ? 0 : tabIndex,
    onTabChange: (idx) => {
      setTab(tabList[idx]);
      haptic(40);
    },
    enabled: true,
    containerEl: panelsViewportEl,
    tabsRef: tabsBarRef,
    indicatorRef,
    onDrag: (offset, targetIdx) => {
      const idx = tabList.indexOf(tab);
      applyPanelsSlide(idx === -1 ? 0 : idx, offset, false);
    },
    onDragEnd: (committed) => {
      if (!committed) {
        const idx = tabList.indexOf(tab);
        applyPanelsSlide(idx === -1 ? 0 : idx, 0, true);
        requestAnimationFrame(() => snapIndicator(idx === -1 ? 0 : idx));
      }
    },
  });

  // Sort + filter state — Class-B restorable view state owned by uiStateStore
  // (Phase 2, #1979). This component is a pure renderer: reads via selectors,
  // writes via the named store actions (R-A2 — no shadow useState).
  const sortBy = useUiState((s) => s.sessionListSort);
  const showInProgress = useUiState((s) => s.sessionListShowInProgress);
  const showAutonomous = useUiState((s) => s.sessionListShowAutonomous);

  // ── session-list FSM derived selectors (Phase 2) ─────────────────────────
  // The "In Progress" and "Agent" counts now come from cross-store selectors
  // over `sessionListStore × composerStore`. The pill below reads
  // `anyBusyListed` from the same store pair. This is the FE-4 desync cure:
  // a single answer per render, no `sessions[] × busySet` ad-hoc cross-ref.
  // (`useSocket.ts` reconciles `sessions[]` + `sessionMeta` into
  // sessionListStore via a single mirror useEffect — Phase 3 replaces that
  // mirror with per-site direct store actions.)
  const inProgressCount = useActiveUserCount();
  const agentBusyCount = useActiveAgentCount();
  const anyBusyListed = useAnyBusyListed();
  // Listed user + agent entries from sessionListStore — the main filter memo
  // reads these instead of the legacy `sessions[]` prop, killing the
  // sort-badge drift bug class along with the desync one.
  const userSessions = useUserSessions();
  const agentSessions = useAgentSessions();

  // `autonomousCount` still reads the legacy `sessions` prop — drives the
  // auto-deactivate of the "Agent" filter when no agent sessions exist at all
  // (regardless of busy state). Migrating it would require an extra selector;
  // the cross-ref isn't a desync risk because no busy intersection is involved.
  const autonomousCount = useMemo(
    () => sessions.filter((s) => s.status !== 'cleared' && isAgentSource((s as any).source)).length,
    [sessions],
  );

  // Auto-deactivate the filter when nothing is in progress anymore
  useEffect(() => {
    if (inProgressCount === 0 && showInProgress) setSessionListFilters({ showInProgress: false });
  }, [inProgressCount, showInProgress]);

  // Auto-deactivate agent filter when none exist
  useEffect(() => {
    if (autonomousCount === 0 && showAutonomous) setSessionListFilters({ showAutonomous: false });
  }, [autonomousCount, showAutonomous]);

  // Deploy-gate marker now reads from the wave/plans FSM store (#1657 Phase 2b).
  // useSocket routes PHASE_DEPLOY_REQUIRED/DETECTED + wave_complete/failed into the
  // store, which owns the awaitingDeploy transitions — replaces the old direct
  // `va:wave-event` window listener (R-A2). Fine-grained selector: a wave_started
  // event (which doesn't touch the flag) never re-renders this list.
  const awaitingDeploy = useAwaitingDeploy();

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchActive, setSearchActive] = useState(false);
  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const doSearch = useCallback(async (q: string) => {
    if (!q.trim()) { setSearchResults([]); setSearching(false); return; }
    setSearching(true);
    try {
      const { authFetch } = await import('../api/authFetch');
      const res = await authFetch(`/api/sessions/search?q=${encodeURIComponent(q.trim())}&limit=20`);
      if (res.ok) setSearchResults((await res.json()).results || []);
    } catch { /* ignore */ } finally { setSearching(false); }
  }, []);

  const handleSearchInput = useCallback((value: string) => {
    setSearchQuery(value);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    if (!value.trim()) { setSearchResults([]); setSearching(false); return; }
    setSearching(true);
    searchDebounce.current = setTimeout(() => doSearch(value), 350);
  }, [doSearch]);

  const handleSearchClear = useCallback(() => {
    setSearchQuery('');
    setSearchResults([]);
    setSearchActive(false);
    setSearching(false);
    document.dispatchEvent(new CustomEvent('search-deactivated'));
  }, []);

  const handleSearchResultClick = useCallback((result: SearchResult) => {
    if (onSwitchToMessage) onSwitchToMessage(result.sessionId, result.messageId);
    else onSelectSession(result.sessionId);
    handleSearchClear();
  }, [onSwitchToMessage, onSelectSession, handleSearchClear]);

  useEffect(() => {
    if (searchActive && searchInputRef.current) searchInputRef.current.focus();
  }, [searchActive]);

  const { pinnedSessions, unpinnedSessions } = useMemo(() => {
    // Phase 2: source from sessionListStore via useUserSessions/useAgentSessions.
    // Both selectors already filter `status !== 'cleared'`, so the legacy
    // first-pass filter is no longer needed. The showInProgress + showAutonomous
    // toggles short-circuit on top of the typed selectors — no
    // `(s as any).source` cross-ref against a separate busy set, which kills
    // both FE-4 bug classes (sort-badge drift + agents-active pill desync).
    let filtered: SessionSummary[];
    if (showAutonomous && showInProgress) {
      // Legacy semantics: showInProgress excludes autonomous, so the
      // intersection of both filters is the empty set.
      filtered = [];
    } else if (showAutonomous) {
      filtered = agentSessions;
    } else if (showInProgress) {
      filtered = userSessions.filter((s) => busySet.has(s.id));
    } else {
      filtered = [...userSessions, ...agentSessions];
    }
    if (issueFilter != null) filtered = filtered.filter((s) => s.linkedIssueNumber === issueFilter);

    const pinned = filtered
      .filter((s) => s.listPinPosition != null)
      .sort((a, b) => (a.listPinPosition ?? 0) - (b.listPinPosition ?? 0));

    const unpinned = filtered.filter((s) => s.listPinPosition == null);

    if (sortBy === 'name') {
      unpinned.sort((a, b) => {
        const na = (a.name || a.previewText || '').toLowerCase();
        const nb = (b.name || b.previewText || '').toLowerCase();
        return na.localeCompare(nb);
      });
    } else {
      // updatedAt DESC. Stable secondary by id so equal timestamps don't flicker.
      unpinned.sort((a, b) => {
        const aT = a.updatedAt ?? '';
        const bT = b.updatedAt ?? '';
        if (aT > bT) return -1;
        if (aT < bT) return 1;
        return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
      });
    }

    return { pinnedSessions: pinned, unpinnedSessions: unpinned };
  }, [userSessions, agentSessions, sortBy, issueFilter, showInProgress, showAutonomous, busySet]);

  // Phase 1: Sort search results client-side based on active sortBy
  const sortedSearchResults = useMemo(() => {
    if (!searchResults.length) return searchResults;
    if (sortBy === 'name') {
      return [...searchResults].sort((a, b) =>
        (a.sessionName || '').toLowerCase().localeCompare((b.sessionName || '').toLowerCase()),
      );
    }
    if (sortBy === 'recent') {
      return [...searchResults].sort((a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
    }
    return searchResults; // API order (relevance fallback)
  }, [searchResults, sortBy]);

  // Phase 2: Apply in-progress filter to search results (excludes autonomous sessions)
  const filteredSearchResults = useMemo(() => {
    if (!showInProgress) return sortedSearchResults;
    return sortedSearchResults.filter((r) => busySet.has(r.sessionId) && !isAgentSource((r as any).source));
  }, [sortedSearchResults, showInProgress, busySet]);

  // Phase 2: In-progress count scoped to search results when search is active (excludes autonomous)
  const displayInProgressCount = useMemo(() => {
    if (searchActive && searchQuery.trim()) {
      return searchResults.filter((r) => busySet.has(r.sessionId) && !isAgentSource((r as any).source)).length;
    }
    return inProgressCount;
  }, [searchActive, searchQuery, searchResults, inProgressCount, busySet]);

  // Close settings panel on outside tap
  useEffect(() => {
    if (!settingsOpen) return;
    const handler = () => setSettingsOpen(false);
    setTimeout(() => document.addEventListener('click', handler), 0);
    return () => document.removeEventListener('click', handler);
  }, [settingsOpen]);

  // Close swiped row when tapping elsewhere.
  // IMPORTANT: exempt the swiped row itself — let onClick handle that case.
  // Without this exemption, pointerdown fires and re-renders (removing sl-row--swiped)
  // before onClick runs, so onClick sees isSwiped=false and re-opens the tray.
  useEffect(() => {
    if (!swipedRowId) return;
    const close = (e: PointerEvent) => {
      if ((e.target as HTMLElement).closest('.sl-swipe-tray')) return;
      if ((e.target as HTMLElement).closest('.sl-row--swiped')) return;
      setSwipedRowId(null);
    };
    document.addEventListener('pointerdown', close, true);
    return () => document.removeEventListener('pointerdown', close, true);
  }, [swipedRowId]);

  return (
    <>
    <div class="sl-view">

      {/* ── Top bar ── */}
      <div class="sl-topbar">
        <div class="sl-topbar-brand">
          {getBrandId() === 'brato' ? (
            // Legacy Brato wordmark — cartoon letters with per-letter theme switcher.
            // Preserved unchanged so the existing voice.akrsmv.net experience is identical
            // until Phase 5 of the three-product deployment plan.
            <h1 class="brand-link">
              <div class="brand-mark">
                <div class="brand-row-top">
                  <div class="brand-block-brat">
                    <div class="brand-letters-line">
                      <span class={`brand-c brand-B brand-letter${theme === 'B' ? ' theme-active' : ''}`} onClick={() => onThemeSwitch('B')} title="Moss theme">B</span>
                      <span class={`brand-c brand-r brand-letter${!theme || theme === 'r' ? ' theme-active' : ''}`} onClick={() => onThemeSwitch('r')} title="Zen theme">r</span>
                      <span class={`brand-c brand-a brand-letter${theme === 'a' ? ' theme-active' : ''}`} onClick={() => onThemeSwitch('a')} title="Sand theme">a</span>
                      <span class={`brand-c brand-t brand-letter${theme === 't' ? ' theme-active' : ''}`} onClick={() => onThemeSwitch('t')} title="Slate theme">t</span>
                    </div>
                  </div>
                  <div class="brand-tail">
                    <span class="brand-c brand-o-text">o</span>
                    <span
                      class={`brand-signal ${!connected ? 'disconnected' : deployStatus === 'justDeployed' ? 'deploying' : deployStatus === 'rebuildScheduled' ? 'rebuild-scheduled' : (speaking || anyBusyListed || toolActivities.length > 0) ? 'operating' : 'connected'}`}
                      title={!connected ? 'Disconnected' : deployStatus === 'rebuildScheduled' ? formatDeployPending(deployPending) : (speaking || anyBusyListed || toolActivities.length > 0) ? 'Agents active' : 'Connected'}
                    />
                  </div>
                </div>
              </div>
            </h1>
          ) : (
            // Per-brand header for Valko / Protofac / Siynergia. Logo + name; the same
            // connection/operating pip is reused as a stand-alone signal next to the name.
            <h1 class="brand-link brand-link--logo" data-brand={getBrandId()}>
              <img class="brand-logo-img" src={getBrand().logoSrc} alt={`${getBrand().name} logo`} width="32" height="32" />
              <span class="brand-logo-name">{getBrand().name}</span>
              <span
                class={`brand-signal ${!connected ? 'disconnected' : deployStatus === 'justDeployed' ? 'deploying' : deployStatus === 'rebuildScheduled' ? 'rebuild-scheduled' : (speaking || anyBusyListed || toolActivities.length > 0) ? 'operating' : 'connected'}`}
                title={!connected ? 'Disconnected' : deployStatus === 'rebuildScheduled' ? 'Rebuild scheduled' : (speaking || anyBusyListed || toolActivities.length > 0) ? 'Agents active' : 'Connected'}
              />
            </h1>
          )}
        </div>
        <div class="sl-topbar-actions">
          <BeetleLogbookButton visible={!!beetleVisible} onClick={onLogbook ?? (() => {})} />
          {/* Files drawer surfaces workspace files / plans / archive — hide on voice-only brands. */}
          {!getBrand().voiceOnlyMode && (
            <button
              class="sl-topbar-btn"
              title="Files (Temp Docs · Plans · Archive)"
              onClick={() => (onFiles ? onFiles() : openDrawer('files'))}
            >📁</button>
          )}
          <button
            class="sl-topbar-btn"
            title="Settings"
            onClick={() => setSettingsOpen(!settingsOpen)}
          >⚙</button>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div
        class="sl-tabs"
        ref={tabsBarRef}
        role="tablist"
        aria-label="Session list sections"
        onKeyDown={(e: KeyboardEvent) => {
          const idx = tabList.indexOf(tab);
          if (e.key === 'ArrowRight' && idx < tabList.length - 1) {
            const next = tabList[idx + 1];
            setTab(next);
            haptic(40);
          } else if (e.key === 'ArrowLeft' && idx > 0) {
            setTab(tabList[idx - 1]);
            haptic(40);
          }
        }}
      >
        <button
          class={`sl-tab${tab === 'sessions' ? ' active' : ''}`}
          role="tab"
          id="sl-tab-sessions"
          aria-selected={tab === 'sessions'}
          aria-controls="sl-panel-sessions"
          onClick={() => setTab('sessions')}
        >Sessions</button>
        {/* Developer tabs — Plans, Roads — hidden on voice-only brands AND per-tab gated by
            the feature-surface flags (SE-PF4). These surface code/PR/branch/chunk-execution
            UI not for consumer-tier brands. Tasks/Usage/Ops moved to bottom-nav-only (issue #5,
            coach-tooltip-system.md Phase 5) — this drawer keeps the content-scoped set. */}
        {!getBrand().voiceOnlyMode && plansTabEnabled && (
          <button
            class={`sl-tab${tab === 'plans' ? ' active' : ''}`}
            role="tab"
            id="sl-tab-plans"
            aria-selected={tab === 'plans'}
            aria-controls="sl-panel-plans"
            onClick={() => setTab('plans')}
          >Plans</button>
        )}
        {!getBrand().voiceOnlyMode && roadsTabEnabled && (
          <button
            class={`sl-tab${tab === 'roads' ? ' active' : ''}`}
            role="tab"
            id="sl-tab-roads"
            aria-selected={tab === 'roads'}
            aria-controls="sl-panel-roads"
            onClick={() => setTab('roads')}
          >Roads</button>
        )}
        {/* Swipe indicator — positioned by JS, slides proportionally during drag */}
        <div class="sl-tab-indicator" ref={indicatorRef} />
      </div>

      {/* ── Panels viewport (swipe-navigable strip) ── */}
      <div class="sl-panels-viewport" ref={setPanelsViewport}>
        <div class="sl-panels-track" ref={panelsTrackRef}>

          {/* ── Panel 0: Sessions ── */}
          <div class="sl-panel sl-panel--sessions" id="sl-panel-sessions" role="tabpanel" aria-labelledby="sl-tab-sessions">
            {/* Search bar */}
            <div class="sl-search">
              {searchActive ? (
                <div class="sl-search-row">
                  <input
                    ref={searchInputRef}
                    class="sl-search-input"
                    type="text"
                    placeholder="Search all sessions..."
                    value={searchQuery}
                    onInput={(e) => handleSearchInput((e.target as HTMLInputElement).value)}
                    onKeyDown={(e) => { if (e.key === 'Escape') handleSearchClear(); }}
                  />
                  {searching && <span class="sl-search-spinner" />}
                  <button class="sl-search-close" onClick={handleSearchClear}>&times;</button>
                </div>
              ) : (
                <button class="sl-search-toggle" onClick={() => { setSearchActive(true); document.dispatchEvent(new CustomEvent('search-activated')); }}>
                  <span>&#128269;</span>
                  <span>Search sessions...</span>
                </button>
              )}
            </div>
            {/* Sort bar — always visible (also applies to search results) */}
            <div class="sl-sort-bar">
              <button
                class={`sl-sort-btn${sortBy === 'recent' ? ' active' : ''}`}
                onClick={() => setSessionListSort('recent')}
              >⏱ Recent</button>
              <button
                class={`sl-sort-btn${sortBy === 'name' ? ' active' : ''}`}
                onClick={() => setSessionListSort('name')}
              >A–Z</button>
              <button
                class={`sl-sort-btn sl-sort-btn--inprogress${showInProgress ? ' active' : ''}${displayInProgressCount === 0 ? ' sl-sort-btn--zero' : ''}`}
                onClick={() => { if (displayInProgressCount > 0) setSessionListFilters({ showInProgress: !showInProgress, showAutonomous: false }); }}
              >You · {displayInProgressCount}</button>
              <button
                class={`sl-sort-btn sl-sort-btn--agent${showAutonomous ? ' active' : ''}${autonomousCount === 0 ? ' sl-sort-btn--zero' : ''}${agentBusyCount > 0 && !showAutonomous ? ' sl-sort-btn--agent-busy' : ''}`}
                onClick={() => { if (autonomousCount > 0) setSessionListFilters({ showAutonomous: !showAutonomous, showInProgress: false }); }}
              >Agent {autonomousCount > 0 ? `[${autonomousCount}]` : ''}{agentBusyCount > 0 ? ` · ${agentBusyCount} running` : ''}{awaitingDeploy ? ' · 🚀' : ''}</button>
              {!selectMode && sessions.length > 1 && (
                <button
                  class="sl-sort-btn sl-sort-btn--select"
                  onClick={() => enterSelectMode()}
                  title="Select sessions to bulk delete"
                >Select</button>
              )}
            </div>
            {/* Select mode action bar */}
            {selectMode && (
              <>
                <div class="sl-select-bar">
                  <button class="sl-select-cancel" onClick={exitSelectMode}>Cancel</button>
                  <span class="sl-select-count">{selectedIds.size} selected</span>
                  <button class="sl-select-delete" onClick={deleteSelected} disabled={selectedIds.size === 0}>&#128465; Delete</button>
                </div>
                <div class="sl-bulk-filters">
                  <button class="sl-bulk-chip" onClick={selectAll}>All ({bulkCounts.all})</button>
                  {bulkCounts.d1 > 0 && <button class="sl-bulk-chip" onClick={() => selectOlderThan(1)}>+1d ({bulkCounts.d1})</button>}
                  {bulkCounts.d3 > 0 && <button class="sl-bulk-chip" onClick={() => selectOlderThan(3)}>+3d ({bulkCounts.d3})</button>}
                  {bulkCounts.d7 > 0 && <button class="sl-bulk-chip" onClick={() => selectOlderThan(7)}>+7d ({bulkCounts.d7})</button>}
                  {selectedIds.size > 0 && (
                    <button class="sl-bulk-chip sl-bulk-chip--clear" onClick={() => setSelectedIds(new Set())} title="Deselect all (does not delete anything)" aria-label="Clear selection">&#10005; Clear selection</button>
                  )}
                </div>
              </>
            )}
            {/* Scrollable session list */}
            <div class="sl-list-wrap">
              <div class="sl-list" ref={listRef}>
                {/* Search results */}
                {searchActive && searchQuery.trim() ? (
                  <>
                    {searching && filteredSearchResults.length === 0 && (
                      <div class="sl-empty">Searching...</div>
                    )}
                    {!searching && filteredSearchResults.length === 0 && showInProgress && (
                      <div class="sl-empty">No in-progress sessions match "{searchQuery}"</div>
                    )}
                    {!searching && filteredSearchResults.length === 0 && !showInProgress && (
                      <div class="sl-empty">No results for "{searchQuery}"</div>
                    )}
                    {filteredSearchResults.map((r) => (
                      <div key={r.messageId} class="sl-row sl-search-result" onClick={() => handleSearchResultClick(r)}>
                        <div class="sl-avatar sl-avatar--img" style={{ '--avatar-accent': '#56a0d4' } as any}>
                          <img src="/avatars/default.svg" alt="session" loading="lazy" />
                        </div>
                        <div class="sl-row-body">
                          <div class="sl-row-top">
                            <span class="sl-row-name">{r.sessionName || 'Untitled'}</span>
                            <span class="sl-row-time">{timeAgo(r.createdAt)}</span>
                          </div>
                          <div class="sl-row-preview">
                            {highlightMatch(truncateAround(r.text, searchQuery), searchQuery)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <>
                    {issueFilter != null && (
                      <div class="sl-issue-filter-banner">
                        <span>Sessions for <strong>#{issueFilter}</strong></span>
                        <button class="sl-issue-filter-clear" onClick={() => setSessionListIssueFilter(null)}>✕ Clear</button>
                      </div>
                    )}
                    {[...pinnedSessions, ...unpinnedSessions].map((s, idx) => {
                      const isPinned = s.listPinPosition != null;
                      const isFirstUnpinned = isPinned === false && idx === pinnedSessions.length;
                      const isCurrent = s.id === currentSessionId;
                      const isBusy = busySet.has(s.id);
                      const mb = modelBadge(s.model, s.isRelay);
                      const isSelected = selectedIds.has(s.id);
                      const isStale = !isBusy && (Date.now() - new Date(s.updatedAt).getTime()) > 8 * 60 * 60 * 1000;
                      const pinnedCount = sessions.filter((x) => x.listPinPosition != null).length;

                      return (
                        <>
                          {isFirstUnpinned && pinnedSessions.length > 0 && (
                            <div class="sl-pin-separator" role="separator" aria-label="Pinned sessions above">
                              <span class="sl-pin-separator-icon" aria-hidden="true">&#128204;</span>
                              <span class="sl-pin-separator-line" />
                            </div>
                          )}
                          <div class={`sl-row-swipe-container${swipedRowId === s.id ? ' sl-row-swipe-container--swiped' : ''}`} key={s.id}>
                            {/* Close overlay: covers visible row area when swiped. Not transformed
                                → reliable touch target on all mobile browsers (no translateX quirks). */}
                            {swipedRowId === s.id && (
                              <div
                                class="sl-swipe-close-overlay"
                                onClick={(e) => { e.stopPropagation(); closeTray(); }}
                              />
                            )}
                            {!selectMode && (
                              <div class="sl-swipe-tray">
                                <button
                                  class={`sl-swipe-action${isPinned ? ' sl-swipe-action--unpin' : ' sl-swipe-action--pin'}`}
                                  aria-label={isPinned ? 'Unpin session from top' : 'Pin session to top'}
                                  disabled={!isPinned && pinnedCount >= 10}
                                  onClick={() => {
                                    if (isPinned) { onUnpinSession?.(s.id); } else { onPinSession?.(s.id); }
                                    if ('vibrate' in navigator) navigator.vibrate(15);
                                    closeTray();
                                  }}
                                >
                                  <span class="sl-swipe-action-icon">&#128204;</span>
                                  <span class="sl-swipe-action-label">{isPinned ? 'Unpin' : (pinnedCount >= 10 ? 'Full' : 'Pin')}</span>
                                </button>
                                <button
                                  class="sl-swipe-action sl-swipe-action--delete"
                                  aria-label="Delete session"
                                  onClick={() => handleDeleteRequest(s.id)}
                                >
                                  <span class="sl-swipe-action-icon">&#x1F5D1;</span>
                                  <span class="sl-swipe-action-label">Del</span>
                                </button>
                              </div>
                            )}
                            <div
                              class={`sl-row${isCurrent && !selectMode ? ' sl-row--active' : ''}${isSelected ? ' sl-row--selected' : ''}${isStale ? ' sl-row--stale' : ''}${isPinned ? ' sl-row--pinned' : ''}${swipedRowId === s.id ? ' sl-row--swiped' : ''}`}
                              onClick={(e) => {
                                if (longPressTriggered.current) { longPressTriggered.current = false; return; }
                                if (selectMode) { toggleSelect(s.id); return; }

                                const isSwiped = (e.currentTarget as HTMLElement).classList.contains('sl-row--swiped');

                                // Tap on open tray: close it
                                if (isSwiped) {
                                  closeTray();
                                  return;
                                }

                                // Single tap: navigate to chat
                                if (swipedRowId) closeTray(); // close any other open tray first
                                onSelectSession(s.id);
                              }}
                              onPointerDown={handleLongPressStart(s.id)}
                              onPointerUp={handleLongPressEnd}
                              onPointerCancel={handleLongPressEnd}
                            >
                              <div
                                class="sl-avatar-wrap"
                                onClick={selectMode ? undefined : (e) => { e.stopPropagation(); onSelectSession(s.id); }}
                              >
                                {selectMode ? (
                                  <div class={`sl-select-checkbox${isSelected ? ' sl-select-checkbox--checked' : ''}`}>
                                    {isSelected && <span>&#10003;</span>}
                                  </div>
                                ) : (
                                  <>
                                    <div class={`sl-avatar sl-avatar--img${mb.cls === 'cascade' ? ' sl-avatar--cascade' : ''}`} style={mb.cls !== 'cascade' ? { '--avatar-accent': mb.color } as any : undefined}>
                                      <img src={mb.icon} alt={mb.label} loading="lazy" />
                                      {isBusy && <span class="sl-avatar-busy" />}
                                    </div>
                                    <div class="sl-avatar-label">
                                      <span class="sl-avatar-name">{s.name || 'Chat'}</span>
                                      <span class="sl-avatar-type">{modelShortName(s.model, s.isRelay)}</span>
                                    </div>
                                    {onContinue && !isBusy && (
                                      <button
                                        class="sl-continue-btn"
                                        onClick={(e) => { e.stopPropagation(); onContinue(s.id); }}
                                        title="Continue session"
                                      >&#9654;</button>
                                    )}
                                  </>
                                )}
                              </div>
                              <div class="sl-row-body">
                                <div class="sl-row-preview sl-row-preview--top" onPointerDown={(e) => e.stopPropagation()}>
                                  {/* Prefer the 3-4 word topic tag (re-generated after every operator
                                      prompt). Length-gate at 40 chars filters historical garbage tags
                                      from the prompt-bug fixed in haiku-inline.service.ts (replies were
                                      saved verbatim instead of being rejected). Falls back to the last
                                      user message, then to a turn-count placeholder. */}
                                  {(s.contextTag && s.contextTag.length <= 40)
                                    ? s.contextTag
                                    : (s.previewText || `${s.numTurns || 0} turns`)}
                                </div>
                                <div class="sl-row-footer">
                                  <span class="sl-footer-agent-name" aria-hidden="true">{s.name || 'Chat'}</span>
                                  {isPinned && <span class="sl-pin-badge" aria-hidden="true">&#128204;</span>}
                                  {isAgentSource((s as any).source) && (
                                    <span class="sl-auto-badge" title={(s as any).planName || 'Agent'}>&#9889; {(s as any).planName ? (s as any).planName.slice(0, 20) : 'Agent'}</span>
                                  )}
                                  {s.linkedIssueNumber != null && <span class="sl-issue-badge">#{s.linkedIssueNumber}</span>}
                                  {s.lastInputTokens != null && s.lastContextWindow != null && s.lastContextWindow > 0 && (
                                    <span class={`sl-ctx-pct${Math.round((s.lastInputTokens / s.lastContextWindow) * 100) >= 70 ? ' sl-ctx-warn' : ''}`}>
                                      {Math.round((s.lastInputTokens / s.lastContextWindow) * 100)}%
                                    </span>
                                  )}
                                  <span class="sl-row-time">{timeAgo(s.updatedAt)}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </>
                      );
                    })}
                    {pinnedSessions.length === 0 && unpinnedSessions.length === 0 && showInProgress && (
                      <div class="sl-empty">
                        <div class="sl-empty-icon">&#9203;</div>
                        <div class="sl-empty-title">No active agents</div>
                        <div class="sl-empty-hint">All sessions are idle right now.</div>
                      </div>
                    )}
                    {pinnedSessions.length === 0 && unpinnedSessions.length === 0 && showAutonomous && (
                      <div class="sl-empty">
                        <div class="sl-empty-icon">&#9889;</div>
                        <div class="sl-empty-title">No agent sessions</div>
                        <div class="sl-empty-hint">Execute a plan or start a wave to see agent sessions here.</div>
                      </div>
                    )}
                    {pinnedSessions.length === 0 && unpinnedSessions.length === 0 && !showInProgress && !showAutonomous && (
                      <div class="sl-empty">
                        <div class="sl-empty-icon">&#128640;</div>
                        <div class="sl-empty-title">No sessions yet</div>
                        <div class="sl-empty-hint">Tap <strong>+</strong> to start your first conversation.</div>
                      </div>
                    )}
                  </>
                )}
              </div>{/* end .sl-list */}
              <div class="sl-scroll-rail"><div class="sl-scroll-thumb" ref={thumbRef} /></div>
            </div>{/* end .sl-list-wrap */}
          </div>{/* end .sl-panel--sessions */}

          {/* ── Panel 2: Plans ── gated by SE-PF4 flag + voice-only brand; content lazy-mounted */}
          {!getBrand().voiceOnlyMode && plansTabEnabled && (
          <div class="sl-panel sl-panel--plans" id="sl-panel-plans" role="tabpanel" aria-labelledby="sl-tab-plans">
            {nearTab('plans') && <PlansTab isAdmin={isAdmin ?? false} currentSessionId={currentSessionId} sandboxFsEnabled={sandboxFsEnabled} />}
          </div>
          )}

          {/* ── Panel 3: Roads ── gated by SE-PF4 flag + voice-only brand; content lazy (scans FS) */}
          {!getBrand().voiceOnlyMode && roadsTabEnabled && (
          <div class="sl-panel sl-panel--roads" id="sl-panel-roads" role="tabpanel" aria-labelledby="sl-tab-roads">
            {nearTab('roads') && <RoadsTab />}
          </div>
          )}

        </div>{/* end .sl-panels-track */}
      </div>{/* end .sl-panels-viewport */}

      {/* ── FAB row: [⚙ settings] [+ new session] ── */}
      <div class="sl-fab-container">
        {/* Settings panel — opens above the gear FAB */}
        {settingsOpen && (
          <GearMenu
            placement="bottom"
            userEmail={userEmail}
            onClose={() => setSettingsOpen(false)}
            isAdmin={isAdmin}
            memosEnabled={memosEnabled}
            lessonGeneratorEnabled={lessonGeneratorEnabled}
            billingEnabled={billingEnabled}
            sshConnected={sshConnected}
            onSshConnect={onSshConnect}
            onSshDisconnect={onSshDisconnect}
            onSettings={onSettings}
            onBrowser={browserPanelEnabled ? onBrowser : undefined}
            onMemos={memosEnabled ? () => { window.location.href = '/memos'; } : undefined}
            onLessons={lessonGeneratorEnabled ? () => { window.location.href = '/lessons'; } : undefined}
            guideEnabled={guideEnabled}
            pulseEnabled={pulseEnabled}
            onDoctor={onDoctor}
            onRebuild={onRebuild}
            rebuildEnabled={rebuildEnabled}
            onSignOut={onLogout}
          />
        )}

        {/* Primary action: new session (tap = default model, hold = model picker) */}
        {onNewSession && (
          <div class="sl-fab-ns-wrap" ref={fabWrapRef}>
            {fabPickerOpen && (
              <div class="sl-fab-menu sl-fab-model-menu" ref={fabMenuRef}>
                {/* Provider sections are DERIVED from the shared registry census (fabGroups) —
                    labels/colours/order match the desktop caret picker (SE-3 grouping-parity);
                    a new registry provider renders here with no edit to this file (#1973). */}
                {fabGroups.map((g, gi) => (
                  <Fragment key={g.provider}>
                    <div
                      class="sl-fab-picker-section"
                      style={`color:${g.color}${gi > 0 ? ';border-top:1px solid rgba(255,255,255,0.06);margin-top:2px' : ''}`}
                    >{g.label}</div>
                    {g.items.map(m => (
                      <button
                        key={m.id}
                        type="button"
                        class={`sl-fab-model-item ${m.cls}`}
                        onClick={() => { setFabPickerOpen(false); setTimeout(() => onNewSession(m.id), 0); }}
                      >
                        {m.label} <span class="sl-fab-model-tag">{m.tag}</span>
                        {m.ctx && <span class={`sl-fab-model-ctx${m.native1m ? ' native1m' : ''}`} title={m.ctxTitle ?? undefined}>{m.ctx}</span>}
                      </button>
                    ))}
                  </Fragment>
                ))}
                {/* --- Terminal Relays --- */}
                <div class="sl-fab-picker-section" style="color:#ff9800;border-top:1px solid rgba(255,255,255,0.06);margin-top:2px">Terminal Relays</div>
                <button
                  type="button"
                  class="sl-fab-model-item sl-fab-model-item--base"
                  onClick={() => { setFabPickerOpen(false); setTimeout(() => onNewSession('agy'), 0); }}
                >
                  Agy Relay <span class="sl-fab-model-tag">tmux</span>
                  <span class="sl-fab-model-ctx" title="Terminal session wrapper">shell</span>
                </button>
                <button
                  type="button"
                  class="sl-fab-model-item sl-fab-model-item--base"
                  onClick={() => { setFabPickerOpen(false); setTimeout(() => onNewSession('claude-cli'), 0); }}
                >
                  Claude Code <span class="sl-fab-model-tag">stream-json</span>
                  <span class="sl-fab-model-ctx" title="Structured stream-json relay · resumable across restarts">relay</span>
                </button>
              </div>
            )}
            <button
              type="button"
              class="sl-fab"
              data-coach-id="fab"
              title="New session (hold to pick model)"
              onPointerDown={handleFabPointerDown}
              onPointerUp={handleFabPointerUp}
              onPointerCancel={handleFabPointerUp}
              onClick={handleFabClick}
            >+</button>
          </div>
        )}
      </div>
    </div>

</>
  );
}
